import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3, Users, Eye, ImageIcon, Mail, ShoppingBag, RefreshCw, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function StatCard({ icon: Icon, label, value, sub }) {
    return (
        <div className="border border-white/10 bg-zinc-900/60 p-5">
            <div className="flex items-center justify-between">
                <Icon size={16} className="text-abnormal-red" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{label}</span>
            </div>
            <p className="font-display text-4xl uppercase mt-3">{value}</p>
            {sub && <p className="text-[11px] text-zinc-500 mt-1 uppercase tracking-wider">{sub}</p>}
        </div>
    );
}

function Sparkline({ data, height = 70 }) {
    if (!data?.length) return <div className="h-[70px] flex items-center text-zinc-600 text-xs uppercase tracking-wider">Nessun dato ancora</div>;
    const max = Math.max(...data.map((d) => d.views), 1);
    const w = 100;
    const step = data.length > 1 ? w / (data.length - 1) : w;
    const points = data.map((d, i) => `${i * step},${height - (d.views / max) * (height - 6) - 3}`).join(" ");
    return (
        <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
            <defs>
                <linearGradient id="spark" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                </linearGradient>
            </defs>
            <polygon fill="url(#spark)" points={`0,${height} ${points} ${w},${height}`} />
            <polyline fill="none" stroke="#ef4444" strokeWidth="1.5" points={points} vectorEffect="non-scaling-stroke" />
        </svg>
    );
}

function KVList({ items, empty = "—" }) {
    return items?.length ? (
        <ul className="divide-y divide-white/5">
            {items.map(([k, c]) => (
                <li key={k} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-zinc-200 truncate pr-3">{k}</span>
                    <span className="font-display text-lg text-abnormal-acid shrink-0">{c}</span>
                </li>
            ))}
        </ul>
    ) : <p className="text-zinc-500 text-sm">{empty}</p>;
}

export default function AdminStats() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { nav("/"); return; }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/stats");
            setData(res.data);
        } catch {
            setData(null);
        } finally { setLoading(false); }
    };

    if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-zinc-400">Caricamento…</div>;

    const v = data?.views || {};
    const e = data?.engagement || {};
    const top = data?.top_pages || [];
    const daily = data?.daily || [];
    const ins = data?.insights || {};

    const fmtDur = (s) => {
        if (!s) return "0s";
        if (s < 60) return `${s}s`;
        const m = Math.floor(s / 60), r = s % 60;
        return r ? `${m}m ${r}s` : `${m}m`;
    };

    return (
        <div data-testid="admin-stats-page" className="max-w-[1300px] mx-auto px-5 md:px-10 py-14 md:py-16">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <BarChart3 className="text-abnormal-red" />
                    <h1 className="font-display text-5xl md:text-7xl uppercase leading-none">Stats</h1>
                </div>
                <button
                    data-testid="stats-refresh"
                    onClick={load}
                    disabled={loading}
                    className="border border-white/20 hover:border-abnormal-red hover:text-abnormal-red text-xs uppercase tracking-[0.25em] font-bold px-4 py-2 inline-flex items-center gap-2 transition-colors"
                >
                    <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aggiorna
                </button>
            </div>
            <p className="text-zinc-500 text-sm mt-3 max-w-2xl">
                Analytics interne, privacy-first. Nessun cookie, nessun tracker terzo. I dati scadono dopo 90 giorni.
            </p>

            {/* Views */}
            <section className="mt-10">
                <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3">Visite</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <StatCard icon={Eye} label="Oggi" value={v.today ?? "—"} sub={`${v.uniques_today ?? 0} unici`} />
                    <StatCard icon={Eye} label="7 giorni" value={v.week ?? "—"} sub={`${v.uniques_week ?? 0} unici`} />
                    <StatCard icon={Eye} label="30 giorni" value={v.month ?? "—"} />
                    <StatCard icon={TrendingUp} label="Trend 14 giorni" value={daily.length ? daily.slice(-1)[0].views : "—"} sub="ieri" />
                    <StatCard icon={Users} label="Utenti" value={e.total_users ?? "—"} sub={`+${e.new_signups_week ?? 0} settimana`} />
                </div>
            </section>

            {/* Sparkline */}
            <section className="mt-8 border border-white/10 bg-zinc-900/60 p-5">
                <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3">Ultimi 14 giorni</p>
                <Sparkline data={daily} />
                <div className="grid grid-cols-7 mt-3 gap-1">
                    {daily.slice(-7).map((d) => (
                        <div key={d.day} className="text-center">
                            <p className="text-[10px] text-zinc-500">{d.day.slice(5)}</p>
                            <p className="font-display text-lg">{d.views}</p>
                        </div>
                    ))}
                </div>
            </section>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
                {/* Top pages */}
                <section className="border border-white/10 bg-zinc-900/60 p-5">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Pagine più viste (7 giorni)</p>
                    {top.length === 0 ? (
                        <p className="text-zinc-500 text-sm">Nessun dato ancora.</p>
                    ) : (
                        <div className="divide-y divide-white/5">
                            {top.map((row) => (
                                <div key={row.path} className="flex items-center justify-between py-2.5">
                                    <span className="text-sm text-zinc-200 truncate pr-3">{row.path || "/"}</span>
                                    <span className="font-display text-xl text-abnormal-acid shrink-0">{row.views}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* Engagement */}
                <section className="border border-white/10 bg-zinc-900/60 p-5">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Contenuti</p>
                    <div className="grid grid-cols-2 gap-3">
                        <StatCard icon={ImageIcon} label="Foto rider" value={e.riders ?? "—"} />
                        <StatCard icon={ImageIcon} label="Foto community" value={e.photos ?? "—"} />
                        <StatCard icon={Mail} label="Newsletter" value={e.newsletter ?? "—"} sub="iscritti" />
                        <StatCard icon={ShoppingBag} label="Prodotti shop" value={e.products ?? "—"} />
                    </div>
                </section>
            </div>

            {/* ═══════════ INSIGHTS — sorgenti, device, geo (7 giorni) ═══════════ */}
            <section className="mt-10">
                <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-4">Insight · Ultimi 7 giorni</p>

                {/* Health Score card */}
                {ins.health && (
                    <div className="border border-white/10 bg-zinc-900/60 p-5 mb-4 flex flex-wrap items-center gap-6">
                        <div>
                            <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-1">📊 Health Score</p>
                            <p className="font-mono">
                                <span className={`text-5xl font-bold ${ins.health.score >= 60 ? "text-abnormal-acid" : ins.health.score >= 40 ? "text-orange-400" : "text-abnormal-red"}`}>{ins.health.score}</span>
                                <span className="text-zinc-400 text-sm ml-3">{ins.health.grade}</span>
                            </p>
                        </div>
                        <div className="flex-1 min-w-[240px] space-y-1">
                            {(ins.health.breakdown || []).map(([lbl, val]) => (
                                <div key={lbl} className="flex items-center justify-between text-xs">
                                    <span className="text-zinc-400">{lbl}</span>
                                    <span className="text-abnormal-acid font-mono">{val}/100</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={Users} label="Nuovi" value={ins.new_visitors ?? "—"} sub={`${ins.returning_visitors ?? 0} di ritorno`} />
                    <StatCard icon={TrendingUp} label="Sessione media" value={fmtDur(ins.avg_session_seconds || 0)} />
                    <StatCard icon={Eye} label="Per pagina" value={fmtDur(ins.avg_page_seconds || 0)} />
                    <StatCard icon={BarChart3} label="Landing top" value={ins.top_landings?.[0]?.[0]?.slice(0, 12) || "—"} sub={ins.top_landings?.[0]?.[1] ? `${ins.top_landings[0][1]} sessioni` : ""} />
                </div>

                {/* 4 tiles */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Sorgenti</p>
                        <KVList items={ins.top_sources} empty="Nessun referrer registrato" />
                    </div>
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">🤖 Traffico da AI · {ins.ai_visits || 0}</p>
                        <KVList items={ins.top_ai_sources} empty="Nessuna visita da ChatGPT / Gemini / Claude / Perplexity / Copilot" />
                    </div>
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Landing pages</p>
                        <KVList items={ins.top_landings} />
                    </div>
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Device</p>
                        <KVList items={ins.top_devices} />
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3 mt-4">Browser</p>
                        <KVList items={ins.top_browsers} />
                    </div>
                </div>

                {/* Paesi + Città */}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Paesi</p>
                        <KVList items={ins.top_countries} />
                    </div>
                    <div className="border border-white/10 bg-zinc-900/60 p-5">
                        <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Città</p>
                        <KVList items={ins.top_cities} />
                    </div>
                </div>

                {/* Recent sessions */}
                <div className="mt-6 border border-white/10 bg-zinc-900/60 p-5">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold mb-3">Sessioni recenti</p>
                    {(!ins.recent_sessions || ins.recent_sessions.length === 0) ? (
                        <p className="text-zinc-500 text-sm">Ancora nessuna sessione tracciata.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                        <th className="text-left py-2 pr-3">Sorgente</th>
                                        <th className="text-left py-2 pr-3">Paese · Device</th>
                                        <th className="text-left py-2 pr-3">Percorso</th>
                                        <th className="text-right py-2">Durata</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ins.recent_sessions.map((s, i) => (
                                        <tr key={i} className="border-t border-white/5">
                                            <td className="py-2 pr-3 text-abnormal-acid font-vt tracking-widest">{s.source}{s.is_new ? " ·new" : ""}</td>
                                            <td className="py-2 pr-3 text-zinc-300">{s.country} · {s.device}</td>
                                            <td className="py-2 pr-3 text-zinc-400 font-mono text-xs">{(s.paths || []).slice(0, 5).join(" → ")}{(s.paths || []).length > 5 ? " …" : ""}</td>
                                            <td className="py-2 text-right text-white font-bold">{fmtDur(s.duration_seconds)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
